<template>
  <div>
    <reports-loader
      :client-id="clientId"
      :filters="filters"
      :result="{ items: reports, total: pagination.rowsNumber }"
      @update:result="onLoaded"
    />
    <q-table
      :columns="columns"
      :rows="reports"
      v-model:pagination="pagination"
      wrap-cells
      @request="pagination = $event.pagination"
    >
      <template v-slot:body-cell-name="props">
        <q-td :props="props">
          <router-link :to="{name: 'report', params: {id: props.row.id}}" class="q-link">
            {{ props.value }}
          </router-link>
        </q-td>
      </template>
      <template v-slot:body-cell-dateRange="props">
        <q-td :props="props">
          <date-range
            :date-of-loss="props.row.client.dateOfLoss"
            :range="props.value" />
        </q-td>
      </template>
      <template v-slot:body-cell-postStatus="props">
        <q-td :props="props">
          <span v-if="props.row.source">
            {{ props.row.source }}
          </span>
          <span v-else>
            {{ $filters.postStatus(props.row) }}
          </span>
        </q-td>
      </template>
      <template v-slot:body-cell-createdAt="props">
        <q-td :props="props">
          <humanize-time :time="props.value" />
        </q-td>
      </template>
      <template v-slot:body-cell-lastModified="props">
        <q-td :props="props">
          <humanize-time :time="props.value" />
        </q-td>
      </template>
      <template v-slot:body-cell-actions="props">
        <q-td :props="props">
          <download-btn :report-id="props.row.id" label="" class="on-left" flat round />
          <delete-report-btn :report="props.row" flat round @deleted="remove" />
        </q-td>
      </template>
      <template v-slot:no-data>
        <message type="info" class="full-width q-my-sm" style="font-size: .9rem">
          No reports found.
        </message>
      </template>
    </q-table>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import {
  OpenDateRange,
  QTablePagination,
  QueryResult,
  Report, ReportFilters,
} from '@/type';
import HumanizeTime from '@/components/HumanizeTime.vue';
import { getStatusText } from '@/social/post/post';
import Message from '@/components/message/Message.vue';
import DateRange from './DateRange.vue';
import DeleteReportBtn from './DeleteReportBtn.vue';
import DownloadBtn from './download/DownloadBtn.vue';
import ReportsLoader from './ReportsLoader.vue';
import { types as reportTypes } from '.';

interface Data {
  columns: object[],
  pagination: QTablePagination,
  reports: Report[],
}

export default defineComponent({
  components: {
    Message,
    ReportsLoader,
    HumanizeTime,
    DateRange,
    DeleteReportBtn,
    DownloadBtn,
  },
  props: {
    clientId: {
      type: Number,
      required: true,
    },
  },
  data(): Data {
    return {
      columns: [
        {
          name: 'name',
          label: 'Name',
          field: 'name',
          align: 'left',
        },
        {
          name: 'dateRange',
          label: 'Date Range',
          field(r: Report): OpenDateRange {
            return {
              start: r.startDate,
              end: r.endDate,
            };
          },
          align: 'left',
        },
        {
          name: 'postStatus',
          label: 'Posts',
          align: 'left',
        },
        {
          name: 'createdAt',
          label: 'Created',
          field: 'createdAt',
          align: 'left',
        },
        {
          name: 'lastModified',
          label: 'Last Modified',
          field: 'lastModified',
          align: 'left',
        },
        {
          name: 'actions',
          label: '',
          align: 'right',
        },
      ],
      pagination: {
        page: 1,
        rowsPerPage: 5,
        rowsNumber: 0,
      },
      reports: [],
    };
  },
  computed: {
    filters(): ReportFilters {
      return {
        types: [reportTypes.LEGAL],
        offset: (this.pagination.page - 1) * this.pagination.rowsPerPage,
        limit: this.pagination.rowsPerPage,
      };
    },
  },
  methods: {
    onLoaded(result: QueryResult) {
      this.reports = (result.items as Report[]);
      this.pagination.rowsNumber = result.total;
    },
    prepend(report: Report) {
      this.reports.unshift(report);
      this.pagination.rowsNumber += 1;
    },
    set(report: Report) {
      const index = this.reports.findIndex(r => r.id === report.id);
      if (index >= 0) {
        this.reports.splice(index, 1, report);
      }
    },
    remove(report: Report) {
      const index = this.reports.findIndex(r => r.id === report.id);
      if (index >= 0) {
        this.reports.splice(index, 1);
        this.pagination.rowsNumber -= 1;
      }
    },
  },
  filters: {
    postStatus(report: Report): string {
      const statuses = report.statuses.map(getStatusText);
      if (report.isOnTimeline) {
        statuses.unshift('Timeline');
      }
      if (report.isFlagged) {
        statuses.push('Flagged');
      }
      return statuses.join(', ');
    },
  },
});
</script>
